import { Component, type ComponentChildren } from 'preact';
import { Alert } from '../Alert/Alert';
import type { WidgetBuildInfo } from '../../../analytics/types';
import { captureWidgetBoundaryError } from '../../../sentry';
import type { WidgetInitialConfig } from '../../../widgetConfig';

export const WIDGET_ERROR_DOM_EVENT_NAME = 'passenger-widget:error';

export interface WidgetErrorDetail {
  message: string;
  componentStack: string | null;
  widgetVersion: string;
  tenant: string;
}

interface Props {
  hostElement: HTMLElement;
  tenant: string;
  buildInfo: WidgetBuildInfo;
  initialConfig: WidgetInitialConfig;
  children: ComponentChildren;
}

interface State {
  hasError: boolean;
}

export class WidgetErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  componentDidCatch(
    error: Error,
    errorInfo: { componentStack?: string },
  ): void {
    const { hostElement, tenant, buildInfo, initialConfig } = this.props;

    const detail: WidgetErrorDetail = {
      message: error.message,
      componentStack: errorInfo.componentStack ?? null,
      widgetVersion: buildInfo.version,
      tenant,
    };

    hostElement.dispatchEvent(
      new CustomEvent<WidgetErrorDetail>(WIDGET_ERROR_DOM_EVENT_NAME, {
        detail,
        bubbles: true,
        composed: true,
      }),
    );

    captureWidgetBoundaryError({
      error,
      componentStack: errorInfo.componentStack ?? null,
      tenant,
      buildInfo,
      initialConfig,
    });

    this.setState({ hasError: true });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert variant="error">
          Sorry, something went wrong. Please try refreshing the page.
        </Alert>
      );
    }

    return this.props.children;
  }
}
